import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import moment from 'moment';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import Card from '../../../components/card/Card';
import { useChartOptions } from '../../../hooks/useChartOptions';
import { translate } from '../../../i18n';
import ChartReadState, {
    chartHasSomethingToDraw,
} from '../../analytics/components/ChartReadState';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetGrowthTrajectory } from '../hooks/useGetGrowthTrajectory';
import { classifyRead } from '../../_shared/readState';

export default function GrowthTrajectoryChart() {
    const { selectedLocation } = useSelectedLocation();
    const { data, isError, isInitialLoading, refetch } = useGetGrowthTrajectory(
        selectedLocation?.id,
    );
    /**
     * A refused read left `data` undefined, so `hasData` was false and this
     * chart said `impact.no_data` — "Not enough data yet", under a hint telling
     * the operator data will appear as their venue starts using MySportSet.
     * That is a claim about their venue, made from a read we were refused.
     */
    /**
     * "Not enough data yet" is a claim about the venue, so it is only honest
     * once the read has actually ANSWERED. Keying it on `hasData` alone said it
     * for a read still in flight and — because these queries are `enabled`-gated
     * and React Query reports a disabled query as loading — for a read that was
     * never issued at all.
     *
     * Run 102 measured this chart as the one `/analytics` chart that never
     * draws an empty chart, and allowlisted it while recording in its own
     * critique that the never-issued arm — a bare em dash, indistinguishable
     * from a loading state — was ambiguous rather than correct. Run 103
     * classifies it properly: `ChartReadState` renders the location prompt for
     * a read nobody sent and keeps the em dash for the one still in flight.
     */
    const readState = classifyRead(isError, isInitialLoading, data);
    const answered = readState === 'answered';
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = 'secondaryGray.600';

    const seriesData: ApexAxisChartSeries = useMemo(() => {
        const rows = data ?? [];
        return [
            {
                name: translate('impact.bookings_axis'),
                type: 'column',
                data: rows.map((d) => ({ x: new Date(d.period).getTime(), y: d.bookings })),
            },
            {
                name: translate('impact.new_players_axis'),
                type: 'column',
                data: rows.map((d) => ({ x: new Date(d.period).getTime(), y: d.newPlayers })),
            },
            {
                name: translate('impact.revenue_axis'),
                type: 'line',
                data: rows.map((d) => ({ x: new Date(d.period).getTime(), y: d.revenue })),
            },
        ];
    }, [data]);

    const baseChart = useChartOptions();

    const options: ApexCharts.ApexOptions = {
        ...baseChart,
        chart: { ...(baseChart.chart ?? {}), type: 'line', stacked: false },
        stroke: { width: [0, 0, 3], curve: 'smooth' },
        plotOptions: {
            bar: {
                columnWidth: '55%',
                borderRadius: 4,
            },
        },
        xaxis: {
            type: 'datetime',
            labels: {
                formatter: (val) => moment(Number(val)).format('MMM DD'),
            },
        },
        yaxis: [
            {
                seriesName: translate('impact.bookings_axis'),
                title: { text: translate('impact.bookings_axis') },
                labels: { formatter: (val) => (val != null ? val.toFixed(0) : '0') },
            },
            {
                seriesName: translate('impact.new_players_axis'),
                show: false,
            },
            {
                opposite: true,
                seriesName: translate('impact.revenue_axis'),
                title: { text: translate('impact.revenue_axis') },
                labels: { formatter: (val) => (val != null ? val.toFixed(0) : '0') },
            },
        ],
        tooltip: {
            ...baseChart.tooltip,
            shared: true,
            x: {
                formatter: (val) => moment(val).format('MMM DD, YYYY'),
            },
        },
        legend: { position: 'top' },
    };

    const hasData = (data ?? []).length > 0;

    return (
        <Card alignItems='center' flexDirection='column' w='100%'>
            <Flex align='flex-start' w='100%' px='15px' py='10px' direction='column'>
                <Text color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    {translate('impact.growth_trajectory')}
                </Text>
                <Text color={textColorSecondary} fontSize='sm' mt='4px'>
                    {translate('impact.growth_trajectory_hint')}
                </Text>
            </Flex>

            <Box h='350px' mt='auto' w='100%'>
                {/*
                  * The guard comes FIRST, ahead of the chart, which is the
                  * ordering `analytics-read-error-gate`'s PIN 1 requires of
                  * every chart in this population. `hasData` happens to be
                  * strictly stronger than the guard here — rows present
                  * implies the read answered — so the nesting order does not
                  * change what renders in any of the four states; it makes the
                  * file legible to the pin, and to the next reader, as one of
                  * the seven rather than as a special case.
                  */}
                {!chartHasSomethingToDraw(readState) ? (
                    <ChartReadState
                        state={readState}
                        gate='location'
                        onRetry={() => {
                            refetch();
                        }}
                    />
                ) : hasData ? (
                    <ReactApexChart
                        options={options}
                        series={seriesData}
                        type='line'
                        height={350}
                    />
                ) : (
                    <Flex h='100%' align='center' justify='center' direction='column' gap='4px'>
                        <Text color={textColor} fontSize='md' fontWeight='600'>
                            {answered ? translate('impact.no_data') : '—'}
                        </Text>
                        {answered && (
                            <Text color={textColorSecondary} fontSize='sm'>
                                {translate('impact.no_data_hint')}
                            </Text>
                        )}
                    </Flex>
                )}
            </Box>
        </Card>
    );
}
